import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { notify, fmtError } from "@/lib/notify";
import AiText from "@/components/AiText";
import {
  Activity,
  RefreshCw,
  Sparkles,
  Hourglass,
  AlertTriangle,
  Plus,
  Edit3,
  Trash2,
  ListChecks,
  UserPlus,
  ArrowUpRight,
  Loader2,
  Check,
  ClipboardCheck,
} from "lucide-react";

/**
 * ContextSyncJournal — Journal des micro-enregistrements (Doctrine 12 · transparence).
 *
 * V23.15.22 — Affiche les 20 derniers events captés par la synchronisation
 * de conscience du Copilote IA. Permet à l'utilisateur de voir EXACTEMENT
 * ce que Noam sait du workspace, sans confiance aveugle.
 */

const ACTION_META = {
  create: { icon: Plus, color: "#10B981", label: "Création" },
  update: { icon: Edit3, color: "#378ADD", label: "Modification" },
  delete: { icon: Trash2, color: "#E24B4A", label: "Suppression" },
};

const MODULE_LABELS = {
  risques: "Risques",
  annuaire: "Annuaire",
  origination: "Origination",
  negociation: "Négociation",
  plans: "Plans d'action",
  controles: "Contrôles",
  incidents: "Incidents",
  alertes: "Alertes",
  fournisseurs: "Fournisseurs",
  filtres: "Filtres",
};

const MODULE_ROUTES = {
  origination: "/app/origination",
  annuaire: "/app/annuaire",
  negociation: "/app/negociation",
  plans: "/app/plans",
  controles: "/app/controles",
  incidents: "/app/incidents",
  alertes: "/app/alertes",
  fournisseurs: "/app/fournisseurs",
  risques: "/app/risques",
};

const LEAD_MODULES = new Set(["origination", "annuaire", "negociation", "fournisseurs"]);

export default function ContextSyncJournal() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [j, s] = await Promise.all([
        api.get("/workspace/context-sync/journal?limit=20"),
        api.get("/workspace/context-sync/status"),
      ]);
      setData(j.data);
      setStatus(s.data);
    } catch (e) {
      notify.error(fmtError(e, "Journal indisponible"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    // Refresh toutes les 30s — c'est un journal "live" mais on évite le polling agressif
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const items = data?.items || [];
  const total = data?.total ?? 0;
  const todayCount = data?.today_count ?? 0;
  const lastSyncedAt = data?.last_synced_at;
  const effectiveOn = status?.effective_enabled !== false;

  return (
    <div className="space-y-6 p-6 max-w-4xl" data-testid="context-sync-journal-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="noam-overline">Doctrine 12 · Transparence</div>
          <h1 className="noam-display text-2xl font-semibold mt-1">
            Journal de synchronisation de conscience
          </h1>
          <p className="text-sm text-zinc-300 mt-2 max-w-2xl">
            Voici ce que Noam a capté des modifications de votre workspace, dans l&apos;ordre
            chronologique inverse. Le Copilote IA utilise ces enregistrements pour rester aligné
            avec votre réalité actuelle, sans que vous ayez besoin de la lui répéter.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          className="px-3 py-2 rounded-md border border-white/10 hover:bg-white/5 text-xs flex items-center gap-2"
          data-testid="context-sync-refresh-btn"
          disabled={loading}
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          Actualiser
        </button>
      </div>

      {/* Banner statut */}
      <div className="noam-card p-4 flex items-center gap-6 flex-wrap" data-testid="context-sync-journal-banner">
        <div className="flex items-center gap-2">
          {effectiveOn ? (
            <Activity className="w-4 h-4 text-emerald-400" />
          ) : (
            <AlertTriangle className="w-4 h-4 text-[#EF9F27]" />
          )}
          <span className="text-xs text-zinc-300">
            {effectiveOn ? "Synchronisation active" : "Synchronisation désactivée"}
          </span>
        </div>
        <div className="h-6 w-px bg-white/10" />
        <div className="text-[11px] text-zinc-500">
          Aujourd&apos;hui : <span className="text-zinc-200 font-medium">{todayCount}</span> capture(s)
        </div>
        <div className="text-[11px] text-zinc-500">
          Total cumulé : <span className="text-zinc-200 font-medium">{total}</span>
        </div>
        {lastSyncedAt && (
          <div className="text-[11px] text-zinc-500">
            Dernière capture :{" "}
            <span className="text-zinc-200 font-medium">
              {new Date(lastSyncedAt).toLocaleString("fr-FR", {
                day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit",
              })}
            </span>
          </div>
        )}
      </div>

      {/* Liste des events */}
      {loading && items.length === 0 ? (
        <div className="noam-card p-8 text-center text-sm text-zinc-500" data-testid="context-sync-loading">
          <Hourglass className="w-5 h-5 mx-auto mb-3 animate-pulse text-[#378ADD]" />
          Chargement du journal…
        </div>
      ) : items.length === 0 ? (
        <div className="noam-card p-8 text-center" data-testid="context-sync-empty">
          <Sparkles className="w-6 h-6 mx-auto mb-3 text-zinc-600" />
          <div className="text-sm text-zinc-300 mb-1">Aucune capture pour l&apos;instant</div>
          <p className="text-xs text-zinc-500 max-w-md mx-auto">
            Dès que vous ou un membre de votre workspace modifiera un risque, une fiche Annuaire,
            une opportunité Origination ou tout autre élément suivi, le Copilote IA le capturera ici.
          </p>
        </div>
      ) : (
        <ul className="space-y-2" data-testid="context-sync-journal-list">
          {items.map((ev, idx) => (
            <JournalRow key={`${ev.ts}-${idx}`} event={ev} />
          ))}
        </ul>
      )}
    </div>
  );
}

function JournalRow({ event }) {
  const navigate = useNavigate();
  const meta = ACTION_META[event.action] || ACTION_META.update;
  const Icon = meta.icon;
  const moduleLabel = MODULE_LABELS[event.module] || event.module;
  const ts = event.ts ? new Date(event.ts) : null;
  const tsDisplay = ts
    ? ts.toLocaleString("fr-FR", {
        day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit",
      })
    : "—";

  const [plan, setPlan] = useState(null);
  const [planBusy, setPlanBusy] = useState(false);
  const [leadState, setLeadState] = useState(null);
  const [savePlanState, setSavePlanState] = useState(null);

  const payload = {
    module: event.module || "",
    entity_id: event.entity_id || "",
    label: event.label || event.entity_id || "",
    summary: event.summary || "",
  };

  const genPlan = async () => {
    if (plan) { setPlan(null); return; }
    setPlanBusy(true);
    try {
      const r = await api.post("/workspace/context-sync/action-plan", payload);
      setPlan(r.data?.plan || "Aucun plan généré.");
    } catch (e) { notify.error(fmtError(e, "Plan d'action indisponible.")); }
    finally { setPlanBusy(false); }
  };

  const toLead = async () => {
    setLeadState("loading");
    try {
      const r = await api.post("/workspace/context-sync/to-lead", payload);
      setLeadState("done");
      notify.success(r.data?.already_exists ? "Déjà dans vos Leads." : "Converti en Lead ✓");
    } catch (e) { notify.error(fmtError(e, "Conversion impossible.")); setLeadState(null); }
  };

  const openModule = () => {
    const route = MODULE_ROUTES[event.module];
    if (route) navigate(route);
    else notify.error("Module non navigable.");
  };

  const savePlan = async () => {
    setSavePlanState("loading");
    try {
      await api.post("/workspace/context-sync/save-plan", {
        label: payload.label, plan, module: payload.module, entity_id: payload.entity_id,
      });
      setSavePlanState("done");
      notify.success("Plan enregistré dans Plans d'action ✓");
    } catch (e) { notify.error(fmtError(e, "Enregistrement impossible.")); setSavePlanState(null); }
  };

  return (
    <li
      className="noam-card p-3"
      data-testid={`context-sync-journal-row-${event.module}-${event.action}`}
    >
      <div className="flex items-start gap-3">
        <div
          className="shrink-0 w-8 h-8 rounded-md flex items-center justify-center"
          style={{ backgroundColor: `${meta.color}15`, color: meta.color }}
        >
          <Icon className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span
              className="text-[10px] uppercase tracking-wider font-medium px-1.5 py-0.5 rounded"
              style={{ backgroundColor: `${meta.color}15`, color: meta.color }}
            >
              {moduleLabel}
            </span>
            <span className="text-[10px] text-zinc-500">{meta.label}</span>
            <span className="text-[10px] text-zinc-500 ml-auto">{tsDisplay}</span>
          </div>
          <div className="text-sm text-zinc-200 mt-1 truncate" title={event.label}>
            {event.label || event.entity_id || "—"}
          </div>
          {event.summary && (
            <div className="text-[11px] text-zinc-500 mt-0.5 truncate" title={event.summary}>
              {event.summary}
            </div>
          )}
          {event.who && (
            <div className="text-[10px] text-zinc-600 mt-1">par {event.who}</div>
          )}

          {/* Bloc d'actions — ferme la boucle détection → action */}
          <div className="flex items-center gap-1.5 mt-2.5 pt-2.5 border-t border-white/10 flex-wrap">
            <button
              type="button"
              onClick={genPlan}
              disabled={planBusy}
              className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded border inline-flex items-center gap-1 transition-colors disabled:opacity-60 ${plan ? "border-[#EF9F27]/50 text-[#EF9F27] bg-[#EF9F27]/5" : "border-white/15 text-zinc-300 hover:text-[#EF9F27] hover:border-[#EF9F27]/50"}`}
              data-testid="capture-action-plan-btn"
              title="Générer un plan d'action IA (ancré doctrine)"
            >
              {planBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <ListChecks className="w-3 h-3" />}
              {plan ? "Masquer le plan" : "Plan d'action"}
            </button>
            {LEAD_MODULES.has(event.module) && (
              <button
                type="button"
                onClick={toLead}
                disabled={leadState === "loading" || leadState === "done"}
                className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded border inline-flex items-center gap-1 transition-colors disabled:opacity-60 ${leadState === "done" ? "border-emerald-500/50 text-emerald-400 bg-emerald-500/5" : "border-white/15 text-zinc-300 hover:text-[#378ADD] hover:border-[#378ADD]/50"}`}
                data-testid="capture-to-lead-btn"
                title="Convertir en Lead (origination)"
              >
                {leadState === "loading" ? <Loader2 className="w-3 h-3 animate-spin" /> : leadState === "done" ? <Check className="w-3 h-3" /> : <UserPlus className="w-3 h-3" />}
                {leadState === "done" ? "Lead créé" : "→ Lead"}
              </button>
            )}
            {MODULE_ROUTES[event.module] && (
              <button
                type="button"
                onClick={openModule}
                className="text-[10px] uppercase tracking-wider px-2 py-1 rounded border border-white/15 text-zinc-300 hover:text-white hover:border-white/40 inline-flex items-center gap-1 transition-colors"
                data-testid="capture-open-module-btn"
                title={`Ouvrir ${moduleLabel}`}
              >
                <ArrowUpRight className="w-3 h-3" />
                Ouvrir {moduleLabel}
              </button>
            )}
          </div>

          {plan && (
            <div className="mt-2 rounded-lg border border-[#EF9F27]/25 bg-[#EF9F27]/[0.04] p-3" data-testid="capture-action-plan-result">
              <div className="text-[10px] uppercase tracking-wider text-[#EF9F27] mb-1.5 flex items-center gap-1">
                <Sparkles className="w-3 h-3" /> Plan d'action
              </div>
              <AiText>{plan}</AiText>
              <div className="flex items-center gap-2 mt-2 pt-2 border-t border-white/10">
                <button
                  type="button"
                  onClick={savePlan}
                  disabled={savePlanState === "loading" || savePlanState === "done"}
                  className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded border inline-flex items-center gap-1 transition-colors disabled:opacity-60 ${savePlanState === "done" ? "border-emerald-500/50 text-emerald-400 bg-emerald-500/5" : "border-[#1D9E75]/40 text-[#1D9E75] hover:bg-[#1D9E75]/10"}`}
                  data-testid="capture-save-plan-btn"
                  title="Créer un plan suivi (avec échéance) dans le module Plans d'action"
                >
                  {savePlanState === "loading" ? <Loader2 className="w-3 h-3 animate-spin" /> : savePlanState === "done" ? <Check className="w-3 h-3" /> : <ClipboardCheck className="w-3 h-3" />}
                  {savePlanState === "done" ? "Enregistré" : "Enregistrer comme Plan"}
                </button>
                {savePlanState === "done" && (
                  <button
                    type="button"
                    onClick={() => navigate("/app/plans")}
                    className="text-[10px] uppercase tracking-wider px-2 py-1 rounded border border-white/15 text-zinc-300 hover:text-white hover:border-white/40 inline-flex items-center gap-1 transition-colors"
                    data-testid="capture-goto-plans-btn"
                  >
                    <ArrowUpRight className="w-3 h-3" /> Voir dans Plans
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </li>
  );
}
